import {
  UntypedFormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonDirective } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { UserAvailabilityService } from '../../../../services/user-availability.service';
import { UtilsService } from '../../../../services/utils.service';
import { CalendarInputComponent } from '../../../shared/calendar-input/calendar-input.component';
import { DialogModule } from 'primeng/dialog';

/**
 * "Add presence for a date range" dialog.
 *
 * The user picks a date range, which weekdays to include, one common daily time
 * range, and any number of breaks (each given as start time + duration). This
 * is expanded on the client into one presence period per (included day x gap
 * between breaks) and sent to the batch endpoint. The backend inserts
 * best-effort and skips periods that overlap existing presence, so existing
 * data is never touched. Editing a single existing period still uses the
 * simpler AvailabilityDialogComponent.
 */
interface WeekdayDef {
  key: string;
  label: string;
  jsDay: number; // Date.getDay(): 0=Sun ... 6=Sat
}

@Component({
  selector: 'app-availability-bulk-dialog',
  templateUrl: './availability-bulk-dialog.component.html',
  styleUrls: ['./availability-bulk-dialog.component.scss'],

  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    CalendarInputComponent,
    Checkbox,
    InputText,
    Textarea,
    InputNumber,
    ButtonDirective,
    ConfirmDialog,
    DialogModule
  ]
})
export class AvailabilityBulkDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  public saving = false;
  public showOverlapDialog = false;

  // Monday-first order for display; jsDay maps to Date.getDay().
  public readonly weekdays: Array<WeekdayDef> = [
    { key: 'mon', label: 'availability.weekday.mon', jsDay: 1 },
    { key: 'tue', label: 'availability.weekday.tue', jsDay: 2 },
    { key: 'wed', label: 'availability.weekday.wed', jsDay: 3 },
    { key: 'thu', label: 'availability.weekday.thu', jsDay: 4 },
    { key: 'fri', label: 'availability.weekday.fri', jsDay: 5 },
    { key: 'sat', label: 'availability.weekday.sat', jsDay: 6 },
    { key: 'sun', label: 'availability.weekday.sun', jsDay: 0 }
  ];

  private userId = '';
  // Guard against accidentally huge ranges.
  private readonly maxRangeDays = 366;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private formBuilder: UntypedFormBuilder,
    private userAvailabilityService: UserAvailabilityService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private confirmationService: ConfirmationService
  ) {
    const weekdayGroup: { [key: string]: any } = {};
    this.weekdays.forEach((w) => {
      weekdayGroup[w.key] = [false];
    });

    this.form = this.formBuilder.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      weekdays: this.formBuilder.group(weekdayGroup),
      startTime: ['08:00', Validators.required],
      endTime: ['16:00', Validators.required],
      breaks: this.formBuilder.array([]),
      description: ['']
    });
  }

  get breaks(): UntypedFormArray {
    return this.form.get('breaks') as UntypedFormArray;
  }

  ngOnInit(): void {
    const data = this.config.data || {};
    this.userId = data.userId;

    const startDate: Date = data.defaultStartDate
      ? new Date(data.defaultStartDate)
      : new Date();
    const endDate: Date = data.defaultEndDate
      ? new Date(data.defaultEndDate)
      : new Date(startDate);

    this.form.patchValue({
      startDate,
      endDate,
      startTime: data.defaultStartTime || '08:00',
      endTime: data.defaultEndTime || '16:00',
      description: data.description || ''
    });

    // Weekday defaults: an explicit single weekday (from a calendar drag-select)
    // wins; otherwise Mon-Fri on, weekend off.
    const weekdaysGroup = this.form.get('weekdays') as UntypedFormGroup;
    if (typeof data.onlyJsDay === 'number') {
      this.weekdays.forEach((w) => {
        weekdaysGroup.get(w.key)?.setValue(w.jsDay === data.onlyJsDay);
      });
    } else {
      this.weekdays.forEach((w) => {
        weekdaysGroup.get(w.key)?.setValue(w.jsDay >= 1 && w.jsDay <= 5);
      });
    }
  }

  public addBreak(): void {
    this.breaks.push(
      this.formBuilder.group({
        startTime: ['12:00', Validators.required],
        durationMinutes: [30, [Validators.required, Validators.min(1)]]
      })
    );
  }

  public removeBreak(index: number): void {
    this.breaks.removeAt(index);
  }

  public doClose(): void {
    this.ref.close(null);
  }

  public save(): void {
    const built = this.buildRows();
    if (built.error) {
      this.utilsService.showError(this.translateService.instant(built.error));
      return;
    }
    if (!built.rows.length) {
      this.utilsService.showError(
        this.translateService.instant('availability.no-days-selected')
      );
      return;
    }
    this.submit(built.rows, false);
  }

  private submit(rows: Array<any>, overwrite: boolean): void {
    this.saving = true;
    this.userAvailabilityService
      .saveUserAvailabilityBatch({ availabilities: rows, overwrite })
      .subscribe(
        (res) => {
          this.saving = false;
          // Conflicts with existing presence: ask before replacing.
          if (res?.disallowed) {
            /*
            this.utilsService.showError(
              this.translateService.instant('availability.overlap-disallowed')
            );
            */
            this.showOverlapDialog = true;
            return;
          }
          if (res?.needsConfirm) {
            this.confirmOverwrite(rows);
            return;
          }
          this.ref.close({
            type: 'batchAdded',
            added: res?.added ?? 0,
            replaced: res?.replaced ?? 0
          });
        },
        (err) => {
          this.saving = false;
          const msg = err?.error?.message || err?.message || 'Save failed';
          this.utilsService.showError(msg);
        }
      );
  }

  private confirmOverwrite(rows: Array<any>): void {
    this.confirmationService.confirm({
      key: 'bulkOverwrite',
      header: this.translateService.instant('availability.overwrite-header'),
      message: this.translateService.instant('availability.overwrite-confirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.button.yes'),
      rejectLabel: this.translateService.instant('common.button.no'),
      accept: () => this.submit(rows, true)
      // reject: keep the dialog open so the user can adjust the range/times.
    });
  }

  /**
   * Expand the form into concrete availability rows. Returns an i18n error key
   * instead of rows when the input is invalid.
   */
  private buildRows(): { rows: Array<any>; error?: string } {
    const v = this.form.getRawValue();
    const startDate = this.toDateOnly(v.startDate);
    const endDate = this.toDateOnly(v.endDate);

    if (!startDate || !endDate) {
      return { rows: [], error: 'availability.invalid-date-range' };
    }
    if (endDate.getTime() < startDate.getTime()) {
      return { rows: [], error: 'availability.invalid-date-range' };
    }

    const dayStart = this.timeToMinutes(v.startTime);
    const dayEnd = this.timeToMinutes(v.endTime);
    if (dayStart === null || dayEnd === null || dayEnd <= dayStart) {
      return { rows: [], error: 'availability.invalid-time-range' };
    }

    // Validate + sort breaks, then split the day into presence segments.
    const rawBreaks = (v.breaks || []).map((b: any) => {
      const s = this.timeToMinutes(b.startTime);
      const dur = Number(b.durationMinutes);
      return { start: s, end: s === null ? null : s + dur, dur };
    });
    for (const b of rawBreaks) {
      if (b.start === null || !(b.dur > 0)) {
        return { rows: [], error: 'availability.invalid-break' };
      }
      if (b.start < dayStart || b.end > dayEnd) {
        return { rows: [], error: 'availability.break-outside-range' };
      }
    }
    rawBreaks.sort((a: any, b: any) => a.start - b.start);
    for (let i = 1; i < rawBreaks.length; i++) {
      if (rawBreaks[i].start < rawBreaks[i - 1].end) {
        return { rows: [], error: 'availability.breaks-overlap' };
      }
    }

    const segments: Array<{ start: number; end: number }> = [];
    let cursor = dayStart;
    for (const b of rawBreaks) {
      if (b.start > cursor) {
        segments.push({ start: cursor, end: b.start });
      }
      cursor = Math.max(cursor, b.end);
    }
    if (dayEnd > cursor) {
      segments.push({ start: cursor, end: dayEnd });
    }

    const enabledDays = new Set<number>();
    const weekdaysGroup = this.form.get('weekdays') as UntypedFormGroup;
    this.weekdays.forEach((w) => {
      if (weekdaysGroup.get(w.key)?.value) {
        enabledDays.add(w.jsDay);
      }
    });

    const rows: Array<any> = [];
    // Use '' (not null) for an empty description to match the single-add dialog;
    // a null title/description renders as the literal text "null" on the calendar.
    const description = v.description || '';
    const cursorDate = new Date(startDate);
    let dayCount = 0;
    while (cursorDate.getTime() <= endDate.getTime()) {
      if (++dayCount > this.maxRangeDays) {
        return { rows: [], error: 'availability.range-too-large' };
      }
      if (enabledDays.has(cursorDate.getDay())) {
        for (const seg of segments) {
          rows.push({
            userId: this.userId,
            availabilityStart: this.combine(cursorDate, seg.start),
            availabilityEnd: this.combine(cursorDate, seg.end),
            description
          });
        }
      }
      cursorDate.setDate(cursorDate.getDate() + 1);
    }

    return { rows };
  }

  private toDateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
      return null;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private timeToMinutes(hhmm: string): number | null {
    if (!hhmm || typeof hhmm !== 'string' || hhmm.indexOf(':') < 0) {
      return null;
    }
    const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
    if (isNaN(h) || isNaN(m)) {
      return null;
    }
    return h * 60 + m;
  }

  private combine(dateOnly: Date, minutes: number): Date {
    return new Date(
      dateOnly.getFullYear(),
      dateOnly.getMonth(),
      dateOnly.getDate(),
      Math.floor(minutes / 60),
      minutes % 60,
      0,
      0
    );
  }

  closeOverlapDialog(): void {
    this.showOverlapDialog = false;
  }
}
